import React, { useEffect, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { Modal, Badge, Spinner, EmptyState } from './ui';
import { enrollmentService } from '../services/api';
import { formatPrice } from '../utils/currency';

/** Drill-down for one teacher's accounting: every approved enrollment
 * across their courses, split into real revenue (a completed Payment
 * already exists) and potential revenue (approved, agreed price set,
 * nothing paid yet). Opened by clicking a teacher row in AdminPayments'
 * "Accounting by Teacher" card. */
export default function TeacherAccountingModal({ teacher, onClose }) {
  const { t } = useTranslation();
  const [data, setData] = useState(null);
  const [loading, setLoading] = useState(false);

  useEffect(() => {
    if (!teacher) return;
    setLoading(true);
    setData(null);
    enrollmentService.getTeacherAccounting(teacher.teacher_id)
      .then((res) => setData(res.data))
      .catch(() => setData(null))
      .finally(() => setLoading(false));
  }, [teacher]);

  if (!teacher) return null;

  return (
    <Modal open={!!teacher} onClose={onClose} title={teacher.teacher_name} size="lg">
      <div className="p-6">
        {loading ? (
          <div className="p-8 flex justify-center"><Spinner /></div>
        ) : !data || data.enrollments.length === 0 ? (
          <EmptyState title={t('No approved enrollments yet')} />
        ) : (
          <>
            <div className="grid grid-cols-2 gap-4 mb-5">
              <div className="bg-emerald-50 rounded-xl p-4">
                <p className="text-xs font-medium text-emerald-700">{t('Real revenue')}</p>
                <p className="text-2xl font-display font-bold text-emerald-800 mt-1">{formatPrice(data.real_total, 'USD')}</p>
              </div>
              <div className="bg-purple-50 rounded-xl p-4">
                <p className="text-xs font-medium text-purple-700">{t('Potential revenue')}</p>
                <p className="text-2xl font-display font-bold text-purple-800 mt-1">{formatPrice(data.potential_total, 'USD')}</p>
              </div>
            </div>

            <div className="space-y-2 max-h-96 overflow-y-auto">
              {data.enrollments.map((row) => (
                <div key={row.enrollment_id} className="flex items-center justify-between border border-gray-100 rounded-lg p-3">
                  <div className="min-w-0">
                    <p className="font-medium text-gray-900 truncate">{row.student_name}</p>
                    <p className="text-xs text-gray-500 truncate">{row.module_name}</p>
                    {row.price_note && <p className="text-xs text-purple-600 mt-0.5">{row.price_note}</p>}
                  </div>
                  <div className="text-right shrink-0 ml-3">
                    <p className="font-semibold text-gray-900">{formatPrice(row.is_paid ? row.paid_amount : row.agreed_price, 'USD')}</p>
                    <Badge tone={row.is_paid ? 'green' : 'purple'}>{row.is_paid ? t('Paid') : t('Potential')}</Badge>
                  </div>
                </div>
              ))}
            </div>
          </>
        )}
      </div>
    </Modal>
  );
}
